import React, { useState, useEffect } from 'react';
import { IoTriangle } from 'react-icons/io5';
import CohortList from './CohortList';
import PropTypes from 'prop-types';
import useModal from './useModal';
import useOrganizationSearch from './useOrganizationSearch';
import HighlightText from './HighlightText';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage, customMessage } from 'actions/internal';
import i18n from 'utils/i18n';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';


const SubGroupItem = ({group, groupIdx, subgroup, subgroupIdx, onCustomMessage, onErrorMessage}) => {
  const { t } = useTranslation();
  const [userCollapsed, setUserCollapsed] = useState(null);
  const { openModal } = useModal();
  const { results, activeQuery } = useOrganizationSearch();
  // Force open only if this subgroup is an ancestor of a matched cohort
  // A direct match on the subgroup name doesn't require expanding — the highlight is enough
  const forceExpanded = !!(results && results.ancestorSubgroupIds.has(subgroup.id));
  // userCollapsed: null = user hasn't toggled yet (use defaults), true/false = explicit user choice
  const expanded = userCollapsed !== null ? !userCollapsed : (forceExpanded || (groupIdx === 0 && subgroupIdx === 0));
  const queryClient = useQueryClient();

  // Reset user toggle when search is cleared
  useEffect(() => {
    if (!results) setUserCollapsed(null);
  }, [results]);

  const remove = useMutation({
    mutationFn: (subgroup) => {
      return axios.delete(Routes.api_dashboard_administration_organizations_destroy_subgroup_path({ subgroup_id: subgroup.id }), { headers: authHeaders() })
    },
    onSuccess: () => {
      onCustomMessage(i18n.t('administration.subGroupItem.deleted'), 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'organization', 'groups', group.id, 'subgroups'] })
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        onErrorMessage(Object.values(err.response.data.errors).flat().join(', '));
      } else {
        onErrorMessage(i18n.t('errors.generic'));
      }
    },
  })

  const isMatch = !!(results && results.matchedSubgroupIds.has(subgroup.id));

  return (
    <>
      <div className={`row-cell${isMatch ? ' row-cell--matched' : ''}`}>
        <span className='cell expand level2' onClick={() => setUserCollapsed(expanded)}>
          <IoTriangle style={{color: 'green'}} />
          {expanded ? (<i className="fa fa-chevron-down"></i>) : (<i className="fa fa-chevron-right"></i>)}
        </span>
        <span className='cell id'>{subgroup.id}</span>
        <span className='cell name'>
          <HighlightText text={subgroup.name} query={activeQuery} />
          {!subgroup.is_na && (
            <>
              {subgroup.can_edit && (
                <a className="edit" onClick={() => openModal('subgroup', group, subgroup)}>
                  <i className="fa fa-pencil"></i>
                  &nbsp;
                  {t('actions.edit')}
                </a>
              )}
              {subgroup.can_delete && (
                <a className="remove" onClick={() => window.confirm(i18n.t('confirm.deleteSubGroup')) && remove.mutate(subgroup)}>
                  <i className="fa fa-trash"></i>
                  &nbsp;
                  {t('actions.remove')}
                </a>
              )}
            </>
          )}
        </span>
      </div>
      {expanded && (<CohortList group={group} subgroup={subgroup} />)}
    </>
  );
}

SubGroupItem.propTypes = {
  group: PropTypes.object.isRequired,
  groupIdx: PropTypes.number.isRequired,
  subgroup: PropTypes.object.isRequired,
  subgroupIdx: PropTypes.number.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  onCustomMessage: PropTypes.func.isRequired,
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, type) => {
      dispatch(customMessage(message, type))
    },
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(SubGroupItem);
